import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

let refreshPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const url = original?.url || "";
    const refreshable = !url.includes("/auth/") || url.includes("/auth/me");
    if (error.response?.status === 401 && !original._retry && refreshable) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise || api.post("/auth/refresh");
        await refreshPromise;
        refreshPromise = null;
        return api(original);
      } catch (e) {
        refreshPromise = null;
      }
    }
    return Promise.reject(error);
  }
);

export const endpoints = {
  auth: {
    me: () => api.get("/auth/me").then((r) => r.data),
    login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
    register: (name, email, password) => api.post("/auth/register", { name, email, password }).then((r) => r.data),
    logout: () => api.post("/auth/logout").then((r) => r.data),
    session: (session_id) => api.post("/auth/session", { session_id }).then((r) => r.data),
    completeOnboarding: () => api.post("/auth/onboarding-complete").then((r) => r.data),
    forgotPassword: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
    resetPassword: (token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data),
  },

  reorder: (collection, ids) => api.post("/reorder", { collection, ids }).then((r) => r.data),
  reorderSopSteps: (sopId, step_ids) => api.post(`/sops/${sopId}/reorder-steps`, { step_ids }).then((r) => r.data),

  tree: () => api.get("/tree").then((r) => r.data),

  folders: () => api.get("/folders").then((r) => r.data),
  createFolder: (d) => api.post("/folders", d).then((r) => r.data),
  updateFolder: (id, d) => api.patch(`/folders/${id}`, d).then((r) => r.data),
  deleteFolder: (id) => api.delete(`/folders/${id}`).then((r) => r.data),

  groups: () => api.get("/groups").then((r) => r.data),
  createGroup: (d) => api.post("/groups", d).then((r) => r.data),

  projects: () => api.get("/projects").then((r) => r.data),
  getProject: (id) => api.get(`/projects/${id}`).then((r) => r.data),
  createProject: (d) => api.post("/projects", d).then((r) => r.data),
  updateProject: (id, d) => api.patch(`/projects/${id}`, d).then((r) => r.data),
  deleteProject: (id) => api.delete(`/projects/${id}`).then((r) => r.data),

  tasks: () => api.get("/tasks").then((r) => r.data),
  createTask: (d) => api.post("/tasks", d).then((r) => r.data),
  updateTask: (id, d) => api.patch(`/tasks/${id}`, d).then((r) => r.data),
  deleteTask: (id) => api.delete(`/tasks/${id}`).then((r) => r.data),

  people: () => api.get("/people").then((r) => r.data),
  createPerson: (d) => api.post("/people", d).then((r) => r.data),
  updatePerson: (id, d) => api.patch(`/people/${id}`, d).then((r) => r.data),
  deletePerson: (id) => api.delete(`/people/${id}`).then((r) => r.data),

  commitments: () => api.get("/commitments").then((r) => r.data),
  createCommitment: (d) => api.post("/commitments", d).then((r) => r.data),
  updateCommitment: (id, d) => api.patch(`/commitments/${id}`, d).then((r) => r.data),

  events: () => api.get("/events").then((r) => r.data),

  importsPreview: (resource, csv_text) => api.post("/imports/preview", { resource, csv_text }).then((r) => r.data),
  importsExecute: (resource, csv_text, mapping, context) => api.post("/imports/execute", { resource, csv_text, mapping, context }).then((r) => r.data),

  inbox: (params = {}) => api.get("/inbox", { params }).then((r) => r.data),
  inboxConvertToTask: (d) => api.post("/inbox/convert-to-task", d).then((r) => r.data),
  inboxConvertToProject: (d) => api.post("/inbox/convert-to-project", d).then((r) => r.data),
  inboxAssign: (d) => api.post("/inbox/assign", d).then((r) => r.data),
  inboxEscalate: (d) => api.post("/inbox/escalate", d).then((r) => r.data),
  inboxArchive: (event_id) => api.post("/inbox/archive", { event_id }).then((r) => r.data),
  inboxUndo: (event_id) => api.post(`/inbox/undo/${event_id}`).then((r) => r.data),

  sops: () => api.get("/sops").then((r) => r.data),
  sopSteps: (id) => api.get(`/sops/${id}/steps`).then((r) => r.data),
  createSop: (d) => api.post("/sops", d).then((r) => r.data),
  createSopStep: (d) => api.post("/sop-steps", d).then((r) => r.data),
  updateSop: (id, d) => api.patch(`/sops/${id}`, d).then((r) => r.data),
  runSop: (id, d) => api.post(`/sops/${id}/run`, d).then((r) => r.data),

  sopRuns: () => api.get("/sop-runs").then((r) => r.data),
  sopRunSteps: (id) => api.get(`/sop-runs/${id}/steps`).then((r) => r.data),
  updateSopRun: (id, d) => api.patch(`/sop-runs/${id}`, d).then((r) => r.data),
  updateSopRunStep: (id, d) => api.patch(`/sop-run-steps/${id}`, d).then((r) => r.data),

  escalations: () => api.get("/escalations").then((r) => r.data),
  updateEscalation: (id, d) => api.patch(`/escalations/${id}`, d).then((r) => r.data),

  reminders: () => api.get("/reminders").then((r) => r.data),
  createReminder: (d) => api.post("/reminders", d).then((r) => r.data),
  updateReminder: (id, d) => api.patch(`/reminders/${id}`, d).then((r) => r.data),
  deleteReminder: (id) => api.delete(`/reminders/${id}`).then((r) => r.data),

  // Messages module (Inbox / Sent / Drafts / Email Templates)
  sentMessages: () => api.get("/sent-messages").then((r) => r.data),
  createSentMessage: (d) => api.post("/sent-messages", d).then((r) => r.data),
  deleteSentMessage: (id) => api.delete(`/sent-messages/${id}`).then((r) => r.data),

  drafts: () => api.get("/drafts").then((r) => r.data),
  createDraft: (d) => api.post("/drafts", d).then((r) => r.data),
  updateDraft: (id, d) => api.patch(`/drafts/${id}`, d).then((r) => r.data),
  deleteDraft: (id) => api.delete(`/drafts/${id}`).then((r) => r.data),

  emailTemplates: () => api.get("/email-templates").then((r) => r.data),
  createEmailTemplate: (d) => api.post("/email-templates", d).then((r) => r.data),
  updateEmailTemplate: (id, d) => api.patch(`/email-templates/${id}`, d).then((r) => r.data),
  deleteEmailTemplate: (id) => api.delete(`/email-templates/${id}`).then((r) => r.data),

  meetings: () => api.get("/meetings").then((r) => r.data),
  createMeeting: (d) => api.post("/meetings", d).then((r) => r.data),
  updateMeeting: (id, d) => api.patch(`/meetings/${id}`, d).then((r) => r.data),
  deleteMeeting: (id) => api.delete(`/meetings/${id}`).then((r) => r.data),

  settings: () => api.get("/settings").then((r) => r.data),
  createSetting: (d) => api.post("/settings", d).then((r) => r.data),
  updateSetting: (id, d) => api.patch(`/settings/${id}`, d).then((r) => r.data),

  morningBrief: () => api.get("/morning-brief").then((r) => r.data),
  morningSummary: () => api.post("/ai/morning-summary").then((r) => r.data),
  projectAnalyze: (project_id) => api.post("/ai/project-analyze", { project_id }).then((r) => r.data),
  projectOutreach: (project_id) => api.post("/ai/project-outreach", { project_id }).then((r) => r.data),
  draftMessage: (person_id, intent) => api.post("/ai/draft-message", { person_id, intent }).then((r) => r.data),
  detectSop: (text) => api.post("/ai/detect-sop", { text }).then((r) => r.data),
  escalationBrief: (escalation_id) => api.post("/ai/escalation-brief", { escalation_id }).then((r) => r.data),

  demoReset: () => api.post("/demo/reset").then((r) => r.data),
  demoClear: () => api.post("/demo/clear").then((r) => r.data),
  demoExport: () => api.get("/demo/export", { responseType: "blob" }).then((r) => r.data),
  demoImport: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/demo/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },

  aiAskStream: async (message, session_id, onChunk) => {
    const res = await fetch(`${API_BASE}/ai/ask`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ message, session_id }),
    });
    if (!res.ok || !res.body) throw new Error("Stream failed");
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      if (chunk) onChunk(chunk);
    }
  },

  // Ask Agent (multi-thread, agentic)
  ask: {
    listThreads: () => api.get("/ask/threads").then((r) => r.data),
    createThread: (message, title) => api.post("/ask/threads", { message, title }).then((r) => r.data),
    getThread: (id) => api.get(`/ask/threads/${id}`).then((r) => r.data),
    addMessage: (id, content) => api.post(`/ask/threads/${id}/messages`, { content }).then((r) => r.data),
    patchThread: (id, patch) => api.patch(`/ask/threads/${id}`, patch).then((r) => r.data),
    deleteThread: (id) => api.delete(`/ask/threads/${id}`).then((r) => r.data),
  },

  // Nango — managed OAuth broker for Gmail, Google Calendar, Slack, Notion,
  // LinkedIn and WhatsApp Business.
  nango: {
    connections: () => api.get("/integrations/nango/connections").then((r) => r.data),
    session:     (provider) => api.post("/integrations/nango/session", { provider }).then((r) => r.data),
    disconnect:  (provider) => api.delete(`/integrations/nango/connections/${provider}`).then((r) => r.data),
    reconcile:   () => api.post("/integrations/nango/reconcile").then((r) => r.data),
    dispatch:    (payload) => api.post("/integrations/nango/dispatch", payload).then((r) => r.data),
    calendarEvents: (params = {}) => api.get("/integrations/nango/calendar/events", { params }).then((r) => r.data),
    createCalendarEvent: (payload) => api.post("/integrations/nango/calendar/events", payload).then((r) => r.data),
  },
};
